import React, { useRef, useState } from 'react';
import { Upload as UploadIcon, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const ACCEPT_MAP = {
  seller_profile:    'image/jpeg,image/jpg,image/png,image/webp',
  seller_cover:      'image/jpeg,image/jpg,image/png,image/webp',
  broadcast:         'image/jpeg,image/jpg,image/png,image/webp',
  product:           'image/jpeg,image/jpg,image/png,image/webp',
  business_document: 'image/jpeg,image/jpg,image/png,image/webp,application/pdf',
};

const ImageUpload = ({
  value,           // current URL string (may be /api/files/xxx or external)
  onChange,        // (url) => void
  kind = 'broadcast',
  label = '이미지',
  aspectClass = 'aspect-video',
  circular = false,
  // BARCODE/WMS A4-1G-1 · 상품 대표 이미지처럼 원본 비율 유지가 필요한 경우 'contain'.
  // 기본은 기존 동작(object-cover)이라 다른 사용처(방송/프로필/커버)는 그대로.
  objectFit = 'cover',
  testId,
}) => {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    setError('');
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      const msg = '파일 크기가 5MB를 초과합니다.';
      setError(msg);
      toast.error(msg);
      return;
    }
    setUploading(true);
    const isReplace = !!value;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      // Do NOT set Content-Type manually — axios auto-generates the correct
      // multipart boundary. Manual override strips the boundary and the server
      // rejects the payload with a generic "이미지 처리에 실패했습니다" toast.
      const res = await api.post('/uploads', fd);
      onChange?.(res.data.url);
      toast.success(isReplace ? '이미지가 변경되었습니다.' : '이미지가 저장되었습니다.');
    } catch (err) {
      const msg = err?.response?.data?.detail || '이미지 처리에 실패했습니다. 다시 시도해주세요.';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    upload(e.dataTransfer.files?.[0]);
  };

  const clear = () => {
    onChange?.(null);
    toast.success('이미지가 삭제되었습니다.');
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400">{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed ${dragOver ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'} rounded-lg overflow-hidden bg-black/5 dark:bg-white/5 ${aspectClass}`}
      >
        {value ? (
          <>
            <SafeImg
              src={value}
              kind={kind === 'seller_cover' ? 'seller_cover' : kind === 'seller_profile' ? 'seller_profile' : kind === 'product' ? 'product' : 'broadcast'}
              className={`w-full h-full ${objectFit === 'contain' ? 'object-contain' : 'object-cover'} ${circular ? 'rounded-full' : ''}`}
              alt={label}
            />
            <button type="button" onClick={clear}
              className="absolute top-2 right-2 p-1 bg-black/70 hover:bg-[#FF3B30] text-white rounded-full"
              data-testid={testId ? `${testId}-clear` : 'img-clear'}>
              <X size={12} />
            </button>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <UploadIcon size={22} className="text-gray-400 mb-1" />
            <p className="text-xs text-gray-500">이미지를 등록해주세요.</p>
            <p className="text-[10px] text-gray-400 mt-1">파일 선택 또는 드래그 앤 드롭 · 최대 5MB</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-xs">
            <Loader2 size={16} className="animate-spin mr-2" />
            이미지를 업로드하고 있습니다.
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept={ACCEPT_MAP[kind]}
          onChange={(e) => upload(e.target.files?.[0])} className="hidden"
          data-testid={testId ? `${testId}-input` : 'img-input'} />
        <button type="button" onClick={() => fileRef.current?.click()}
          className="px-3 py-1.5 text-xs font-bold bg-[#FF3B30] text-white rounded-full"
          data-testid={testId ? `${testId}-pick` : 'img-pick'}>
          {value ? '이미지 변경' : '이미지 등록'}
        </button>
        {value && (
          <button type="button" onClick={clear}
            className="px-3 py-1.5 text-xs font-bold border border-black/10 dark:border-white/10 rounded-full">
            이미지 삭제
          </button>
        )}
      </div>
      {error && <p className="text-xs text-[#FF3B30]" data-testid={testId ? `${testId}-error` : 'img-error'}>{error}</p>}
    </div>
  );
};

export default ImageUpload;
